import { memo, useState, useCallback, type ComponentProps } from "react";
import { FormattedMessage } from "react-intl";

import IllustrationModal from "common/modals/illustration_modal";
import Button from "common/core/button";
import {
  Requirement,
  BlockingRequirement,
  BLOCKING_REQ_MESSAGES,
  isBlockingRequirementKey,
} from "common/meeting/notary/requirements";
import { useNotaryMeetingContext } from "common/meeting/notary/context";
import MeetingCompleteImg from "assets/images/meeting/meeting_complete.svg";
import { NotaryCertificateProvider, CertificateMigrationStatus } from "graphql_globals";

import type { NotaryCompleteMeeting as Meeting } from "./complete_fragment.graphql";
import { DigicertCompleteMeetingModal } from "../digicert_otp_modal";
import { ProofCompleteMeetingModal } from "../proof_otp_modal";

type Reqs = {
  value: number;
  key: ComponentProps<typeof Requirement>["messageKey"];
}[];
type BlockingReqs = {
  value: number;
  key: ComponentProps<typeof BlockingRequirement>["messageKey"];
}[];

type ModalProps = {
  onClose: () => void;
  onCompleteMeeting: () => Promise<unknown>;
  onPostCompleteMeeting: (meetingId: string) => void;
  meeting: Meeting;
  numOfUnConfirmedInstructions: number;
};
type Props = Pick<
  ModalProps,
  "onPostCompleteMeeting" | "onCompleteMeeting" | "meeting" | "numOfUnConfirmedInstructions"
> & {
  notaryUser: {
    notaryProfile: null | {
      certificateProvider: NotaryCertificateProvider;
      certificateMigrationStatus: CertificateMigrationStatus;
    };
  };
  disabled?: boolean;
};

export function constructUiReqs(
  unfulfilledRequirements: Meeting["unfulfilledRequirements"],
  numOfUnConfirmedInstructions: number,
): Reqs {
  const {
    numOfUnverifiedSignerIdentities,
    numOfUnfulfilledDesignations,
    numOfUnfulfilledEnoteDesignations,
    numOfUnfulfilledSignatures,
    numOfUnfulfilledSeals,
    numOfSealsWithoutSignatures,
    numOfMissingOnlineDisclosures,
  } = unfulfilledRequirements;
  return (
    [
      {
        value: numOfUnfulfilledDesignations - (numOfUnfulfilledEnoteDesignations || 0),
        key: "designation",
      },
      {
        value: numOfUnfulfilledSignatures,
        key: "notarySignatures",
      },
      {
        value: numOfUnfulfilledSeals,
        key: "seals",
      },
      {
        value: numOfSealsWithoutSignatures,
        key: "sealsWithoutSignatures",
      },
      {
        value: numOfUnfulfilledEnoteDesignations,
        key: "enoteDesignation",
      },
      {
        value: numOfUnverifiedSignerIdentities,
        key: "signerIdentities",
      },
      {
        value: numOfMissingOnlineDisclosures,
        key: "disclosures",
      },
      {
        value: numOfUnConfirmedInstructions,
        key: "instructions",
      },
    ] as const
  ).filter((req) => req.value);
}

function constructUiBlockingReqs(
  unfulfilledRequirements: Meeting["unfulfilledRequirements"],
): BlockingReqs {
  const { numOfUnverifiedSignerIdentities, numOfMissingRequiredSealsForTransaction } =
    unfulfilledRequirements;
  return (
    [
      {
        value: numOfUnverifiedSignerIdentities,
        key: "signerIdentities",
      },
      {
        value: numOfMissingRequiredSealsForTransaction,
        key: "requiredSeals",
      },
    ] as const
  ).filter((req) => req.value);
}

function CompleteMeetingModal({
  meeting,
  onClose,
  onCompleteMeeting,
  onPostCompleteMeeting,
  numOfUnConfirmedInstructions,
}: ModalProps) {
  const { id: meetingId, unfulfilledRequirements } = meeting;
  const { analytics, simulatedNotaryProfile } = useNotaryMeetingContext();
  const [isLoading, setLoading] = useState(false);

  const requirements = constructUiReqs(unfulfilledRequirements, numOfUnConfirmedInstructions);
  const blockingRequirements: BlockingReqs = constructUiBlockingReqs(
    unfulfilledRequirements,
  ).filter(({ key }) => isBlockingRequirementKey(key));
  const hasBlockingReqs = blockingRequirements.length > 0;
  const missingReq = Boolean(requirements.length);

  const handleComplete = () => {
    setLoading(true);
    return (
      onCompleteMeeting()
        // Finally goes first to avoid a react warning when navigate unmounts this component.
        .finally(() => setLoading(false))
        .then(() => {
          if (!simulatedNotaryProfile) {
            analytics.onCompleteMeeting(missingReq);
          }
          onPostCompleteMeeting(meetingId);
        })
    );
  };
  return (
    <IllustrationModal
      className="NotaryMeetingCompleteModal"
      title={
        missingReq ? (
          <FormattedMessage
            id="edceae78-f4f2-441f-89f6-75966fe19de3"
            defaultMessage="Hm, looks like you're missing requirements..."
          />
        ) : (
          <FormattedMessage
            id="acd3746a-d616-408a-a63e-5590440393c4"
            defaultMessage="All meeting requirements have been fulfilled!"
          />
        )
      }
      src={MeetingCompleteImg}
      buttons={[
        <Button
          key="0"
          buttonColor={missingReq ? "danger" : "action"}
          variant="primary"
          automationId="complete-button"
          onClick={handleComplete}
          isLoading={isLoading}
          disabled={hasBlockingReqs}
        >
          <FormattedMessage
            id="7e2a4337-aadf-4665-ac69-d8ed031c2645"
            defaultMessage="Complete Meeting"
          />
        </Button>,
        <Button
          key="1"
          buttonColor="action"
          variant="secondary"
          automationId="return-to-meeting"
          onClick={onClose}
        >
          <FormattedMessage
            id="5e709af6-39ea-4806-b43c-dce2fcc57f32"
            defaultMessage="Return to Meeting"
          />
        </Button>,
      ]}
    >
      {blockingRequirements.map(({ key, value }) => (
        <BlockingRequirement key={key} value={value} messageKey={key} />
      ))}
      {requirements.map(({ key, value }) => (
        <Requirement
          key={key}
          value={value}
          isBlocking={key in BLOCKING_REQ_MESSAGES}
          messageKey={key}
        />
      ))}
    </IllustrationModal>
  );
}

function CompleteButton({
  onCompleteMeeting,
  onPostCompleteMeeting,
  meeting,
  numOfUnConfirmedInstructions,
  disabled,
  notaryUser,
}: Props) {
  const { refetch } = useNotaryMeetingContext();
  const { certificateProvider, certificateMigrationStatus } = notaryUser.notaryProfile!;
  const isDigicert = certificateProvider === NotaryCertificateProvider.DIGICERT;
  const isProof = certificateMigrationStatus === CertificateMigrationStatus.PROOF_CERTIFICATE;

  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const handleOpen = useCallback(() => {
    setLoading(true);
    return refetch()
      .then(() => {
        setModalOpen(true);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [refetch]);
  const handleClose = useCallback(() => setModalOpen(false), []);

  const renderModal = () => {
    return isProof ? (
      <ProofCompleteMeetingModal
        onClose={handleClose}
        meeting={meeting}
        onCompleteMeeting={onCompleteMeeting}
        onPostCompleteMeeting={onPostCompleteMeeting}
        numOfUnConfirmedInstructions={numOfUnConfirmedInstructions}
      />
    ) : isDigicert ? (
      <DigicertCompleteMeetingModal
        onClose={handleClose}
        meeting={meeting}
        onCompleteMeeting={onCompleteMeeting}
        onPostCompleteMeeting={onPostCompleteMeeting}
        numOfUnConfirmedInstructions={numOfUnConfirmedInstructions}
      />
    ) : (
      <CompleteMeetingModal
        onClose={handleClose}
        meeting={meeting}
        onCompleteMeeting={onCompleteMeeting}
        onPostCompleteMeeting={onPostCompleteMeeting}
        numOfUnConfirmedInstructions={numOfUnConfirmedInstructions}
      />
    );
  };
  return (
    <>
      <Button
        className="NotaryMeetingHeader--CompleteButton"
        onClick={handleOpen}
        automationId="complete-review-button"
        buttonColor="action"
        variant="primary"
        isLoading={loading}
        disabled={disabled}
      >
        <FormattedMessage
          id="922d8116-90d7-44d5-b896-602f44230e8d"
          description="Notary complete meeting button"
          defaultMessage="Complete"
        />
      </Button>
      {modalOpen && renderModal()}
    </>
  );
}

export default memo(CompleteButton);
